/**
 * BusinessDetail.Editable.js
 * Inline-editable text blocks with debounced auto-save.
 * No pencil icon — click the text directly, type, and it persists.
 */
import React, { useRef, useEffect, useCallback, useState } from "react";
import { Copy, Plus, Trash2, Check, Loader2, CloudOff } from "lucide-react";

// ─── Status indicator ─────────────────────────────────────────────────────────
// States: "idle" | "saving" | "saved" | "error"
export function SaveStatus({ state, label }) {
  if (state === "saving") {
    return (
      <span className="flex items-center gap-1.5 text-[10px] text-gray-500" data-testid="save-status-saving">
        <Loader2 size={10} className="animate-spin" /> Guardando…
      </span>
    );
  }
  if (state === "saved") {
    return (
      <span className="flex items-center gap-1.5 text-[10px] text-emerald-500" data-testid="save-status-saved">
        <Check size={10} /> Guardado
      </span>
    );
  }
  if (state === "error") {
    return (
      <span className="flex items-center gap-1.5 text-[10px] text-red-400" data-testid="save-status-error">
        <CloudOff size={10} /> Error — reintentando
      </span>
    );
  }
  return <span className="text-[10px] text-gray-600">{label || ""}</span>;
}

// ─── Hook: debounced async save ───────────────────────────────────────────────
// Calls `saver(value)` 800ms after the last change. Also exposes flush() for blur.
export function useDebouncedSave(saver, { delay = 800, onStateChange } = {}) {
  const timerRef = useRef(null);
  const pendingValueRef = useRef(null);
  const hasPendingRef = useRef(false);

  const setState = (s) => { if (onStateChange) onStateChange(s); };

  const doSave = useCallback(async () => {
    if (!hasPendingRef.current) return;
    const value = pendingValueRef.current;
    hasPendingRef.current = false;
    setState("saving");
    try {
      await saver(value);
      setState("saved");
      // revert to idle after a short while so the UI isn't permanently green
      setTimeout(() => setState("idle"), 1400);
    } catch (e) {
      setState("error");
      // basic retry: queue the same value again for another attempt
      hasPendingRef.current = true;
      pendingValueRef.current = value;
      clearTimeout(timerRef.current);
      timerRef.current = setTimeout(doSave, 2500);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [saver]);

  const queue = useCallback((value) => {
    pendingValueRef.current = value;
    hasPendingRef.current = true;
    clearTimeout(timerRef.current);
    timerRef.current = setTimeout(doSave, delay);
  }, [doSave, delay]);

  const flush = useCallback(() => {
    clearTimeout(timerRef.current);
    if (hasPendingRef.current) doSave();
  }, [doSave]);

  useEffect(() => () => clearTimeout(timerRef.current), []);

  return { queue, flush };
}

// ─── InlineText — single-line editable text ──────────────────────────────────
export function InlineText({ value, onSave, placeholder, className = "", testId }) {
  const ref = useRef(null);
  const [state, setState] = useState("idle");
  const { queue, flush } = useDebouncedSave(onSave, { onStateChange: setState });

  // Sync DOM when the external value changes and the element isn't focused
  useEffect(() => {
    if (ref.current && document.activeElement !== ref.current) {
      ref.current.textContent = value || "";
    }
  }, [value]);

  return (
    <>
      <span
        ref={ref}
        contentEditable
        suppressContentEditableWarning
        spellCheck
        data-testid={testId}
        onInput={(e) => queue(e.currentTarget.textContent)}
        onBlur={flush}
        onKeyDown={(e) => {
          if (e.key === "Enter") { e.preventDefault(); e.currentTarget.blur(); }
        }}
        className={`block outline-none rounded-md px-1 -mx-1 transition-colors focus:bg-white/[0.08] focus:ring-1 focus:ring-white/10 empty:before:content-[attr(data-ph)] empty:before:text-gray-600 ${className}`}
        data-ph={placeholder || "Escribe aquí…"}
      />
      <SaveStatus state={state} />
    </>
  );
}

// ─── InlineTextarea — multi-line editable with auto-grow ──────────────────────
export function InlineTextarea({ value, onSave, placeholder, className = "", testId }) {
  const ref = useRef(null);
  const [state, setState] = useState("idle");
  const { queue, flush } = useDebouncedSave(onSave, { onStateChange: setState });

  useEffect(() => {
    if (ref.current && document.activeElement !== ref.current) {
      ref.current.textContent = value || "";
    }
  }, [value]);

  return (
    <>
      <div
        ref={ref}
        contentEditable
        suppressContentEditableWarning
        spellCheck
        data-testid={testId}
        onInput={(e) => queue(e.currentTarget.textContent)}
        onBlur={flush}
        className={`block outline-none rounded-md px-1 -mx-1 whitespace-pre-wrap transition-colors focus:bg-white/[0.08] focus:ring-1 focus:ring-white/10 empty:before:content-[attr(data-ph)] empty:before:text-gray-600 ${className}`}
        data-ph={placeholder || "Escribe aquí…"}
      />
      <SaveStatus state={state} />
    </>
  );
}

// ─── EditableScriptBlock — card wrapper for a labelled inline field ─────────
export function EditableScriptBlock({
  label, value, onSave, onCopy, placeholder,
  highlight = false, small = false, multiline = false, testId,
}) {
  const Field = multiline ? InlineTextarea : InlineText;
  return (
    <div className={`rounded-xl p-3 ${highlight ? "bg-[#ffc700]/10 border border-[#ffc700]/20" : "bg-white/[0.05] border border-white/[0.07]"}`}>
      <div className="flex justify-between items-center mb-1.5 gap-2">
        <p className={`text-[10px] font-bold uppercase tracking-wide ${highlight ? "text-[#ffc700]" : "text-gray-500"}`}>
          {label}
        </p>
        <div className="flex items-center gap-2">
          {onCopy && (
            <button
              onClick={onCopy}
              className={`${highlight ? "text-[#ffc700]/60 hover:text-[#ffc700]" : "text-gray-600 hover:text-gray-300"} transition-colors`}
              data-testid={testId ? `${testId}-copy` : undefined}
            >
              <Copy size={11} />
            </button>
          )}
        </div>
      </div>
      <Field
        value={value}
        onSave={onSave}
        placeholder={placeholder}
        testId={testId}
        className={`${small ? "text-xs" : "text-xs"} leading-relaxed ${highlight ? "text-white font-semibold text-sm" : "text-gray-200"}`}
      />
    </div>
  );
}

// ─── EditableBullets — editable list with add/remove, inline auto-save ───────
export function EditableBullets({ values, onSave, testId }) {
  const incoming = Array.isArray(values) ? values : [];
  const [localList, setLocalList] = useState(incoming);
  const [state, setState] = useState("idle");
  const { queue, flush } = useDebouncedSave(onSave, { onStateChange: setState });
  const rootRef = useRef(null);
  const pendingFocusRef = useRef(-1);

  // Sync from parent only when no bullet row is currently being edited
  // (prevents the cursor from jumping while the user types).
  useEffect(() => {
    const editing = rootRef.current?.contains(document.activeElement);
    if (!editing) setLocalList(incoming);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [incoming.join("|")]);

  // Focus newly-added bullet after render
  useEffect(() => {
    if (pendingFocusRef.current < 0) return;
    const target = rootRef.current?.querySelector(
      `[data-testid="bullet-${pendingFocusRef.current}"]`
    );
    if (target) target.focus();
    pendingFocusRef.current = -1;
  }, [localList.length]);

  const commit = (next) => {
    setLocalList(next);
    // Persist only non-empty trimmed bullets (max 12)
    const cleaned = next.map((s) => (s || "").trim()).filter(Boolean).slice(0, 12);
    queue(cleaned);
  };

  const updateAt = (i, text) => {
    const next = localList.map((v, idx) => (idx === i ? text : v));
    commit(next);
  };

  const addBullet = () => {
    const next = [...localList, ""];
    pendingFocusRef.current = next.length - 1;
    setLocalList(next);
    // Don't persist yet — wait until user types something
  };

  const removeAt = (i) => {
    const next = localList.filter((_, idx) => idx !== i);
    commit(next);
    flush();
  };

  return (
    <div ref={rootRef} className="bg-white/[0.05] border border-white/[0.07] rounded-xl p-3" data-testid={testId}>
      <div className="flex justify-between items-center mb-2 gap-2">
        <p className="text-[10px] font-bold text-gray-500 uppercase tracking-wide">Bullets de valor</p>
        <div className="flex items-center gap-3">
          <SaveStatus state={state} />
          <button
            onClick={addBullet}
            className="text-gray-500 hover:text-[#FF4F00] flex items-center gap-1 text-[10px] transition-colors"
            data-testid="bullets-add-btn"
          >
            <Plus size={10} /> Agregar
          </button>
        </div>
      </div>
      {localList.length === 0 && (
        <p className="text-[11px] text-gray-600 italic py-1">Sin bullets aún. Agregá uno con el botón de arriba.</p>
      )}
      {localList.map((bullet, i) => (
        <BulletRow
          key={`bullet-row-${i}`}
          index={i}
          value={bullet}
          onChange={(txt) => updateAt(i, txt)}
          onBlur={flush}
          onRemove={() => removeAt(i)}
        />
      ))}
    </div>
  );
}

function BulletRow({ value, onChange, onBlur, onRemove, index }) {
  const ref = useRef(null);
  useEffect(() => {
    if (ref.current && document.activeElement !== ref.current) {
      ref.current.textContent = value || "";
    }
  }, [value]);

  return (
    <div className="flex items-start gap-2 mb-1.5 group">
      <span className="text-[#FF4F00] font-black text-xs mt-0.5 shrink-0">•</span>
      <span
        ref={ref}
        contentEditable
        suppressContentEditableWarning
        spellCheck
        data-testid={`bullet-${index}`}
        onInput={(e) => onChange(e.currentTarget.textContent)}
        onBlur={onBlur}
        onKeyDown={(e) => {
          if (e.key === "Enter") { e.preventDefault(); e.currentTarget.blur(); }
        }}
        className="flex-1 text-xs text-gray-200 outline-none rounded px-1 -mx-1 focus:bg-white/[0.08] focus:ring-1 focus:ring-white/10 empty:before:content-['Escribí_el_bullet…'] empty:before:text-gray-600"
      />
      <button
        onClick={onRemove}
        className="opacity-0 group-hover:opacity-100 text-gray-600 hover:text-red-400 transition-opacity shrink-0"
        data-testid={`bullet-${index}-remove`}
        title="Eliminar bullet"
      >
        <Trash2 size={12} />
      </button>
    </div>
  );
}
